import * as React from "react";
import * as ReactDOM from "react-dom";
import * as _ from "lodash";
import i18n from "i18n";
import classNames from "classnames";
import { Link } from "react-router-dom";
import NumberFormat from "react-number-format";
import { observer } from "mobx-react";

import { WBSStore } from "modules/WBS/WBSStore";
import { ProjectStore } from "modules/project/ProjectStore";
import { TakeOffStore } from "modules/takeOff/TakeOffStore";
import { LoginStore } from "modules/login/LoginStore";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  withStyles,
  CircularProgress,
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import { ToolbarStore } from "modules/WBS/ToolbarStore";
import { ToolbarTaskStore } from "modules/WBS/ToolbarTaskStore";
import { WBSPropertiesStore } from "modules/WBS/WBSPropertiesStore";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import {
  List,
  ScrollSync,
  CellMeasurerCache,
  CellMeasurer,
} from "react-virtualized";
import Alert from "modules/WBS/components/layouts/Alert";
import DescriptionCell from "./DescriptionCell";
import { ELeftMenuTab, ERightMenuTab, ERowType } from "common/enums";
import { roundBy } from "utils/DecimalUtils";
import { formatNumber } from "utils/MathUtils";
import { Error } from "common/icons";

// Desabilita o scroll pelas setas do teclado (para cima e baixo)
var keys = { 38: 1, 40: 1 };
function preventDefault(e) {
  e.preventDefault();
}
function preventDefaultForScrollKeys(e): any {
  if (keys[e.keyCode]) {
    preventDefault(e);
    return false;
  }
}

function disableScroll() {
  window.addEventListener("keydown", preventDefaultForScrollKeys, false);
}

var keyTO,
  ms = 1;

const styles = (theme) => ({
  calcHeight: {
    height: "calc(100vh - 234px)",
  },
  table: {
    fontFamily: "Roboto",
  },
  tableHead: {
    fontSize: "12px",
    color: "#5c5c5c",
    "& th": {
      padding: "15px",
      border: "0px",
    },
  },
  thRef: {
    width: "81px",
    whiteSpace: "nowrap" as any,
  },
  nowRap: {
    whiteSpace: "nowrap" as any,
    "&:last-of-type": {
      paddingRight: "10px",
    },
  },
  thLink: {
    width: "25px",
    padding: "0px !important",
  },
  tableBody: {
    fontSize: "13px",
    color: "#666",
    "& > :first-child": {
      borderTop: "1px solid #e0e0e0",
      overflowX: "hidden !important" as any,
    },
  },
  ref: {
    paddingLeft: "20px !important",
  },
  linkIcon: {
    fontSize: "15px",
    color: "#5c5c5c",
    display: "none",
    margin: "auto",
  },
  linkIconEnabled: {
    fontSize: "15px",
    color: "#5c5c5c",
    display: "block",
    margin: "auto",
  },
  relative: {
    position: "relative" as any,
  },
  icon: {
    position: "absolute" as any,
    fontSize: "19px",
    top: "2px",
    color: "#666666",
    pointerEvents: "none" as any,
  },
  input: {
    outlineStyle: "none" as any,
    paddingRight: "10px",
    fontSize: "13px",
    color: "#666",
    height: "24px",
    width: "100%",
    border: "0",
    backgroundColor: "unset",
  },
  rowRoot: {
    height: "fit-content",
    minHeight: 25,
    "& > :last-child": {
      padding: 0,
      borderRight: "0",
    },
    transition: "all 0.3s",
    "&:hover": {
      backgroundColor: "#FAFAFA",
    },
  },
  cellRoot: {
    padding: "0px",
    borderRight: "1px solid #e0e0e0",
  },
  cellRootDisabled: {
    backgroundColor: "rgba(0,0,0,0.02)",
  },
  cellDisabled: {
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
  },
  link: {
    display: "flex",
    paddingRight: "10px",
    fontSize: "13px",
    "&:visited": {
      color: " #0000EE",
    },
  },
  progress: {
    margin: 0,
    color: "#ABC200",
    width: "90px !important",
    height: "90px !important",
  },
  quantityIcon: {
    position: "absolute" as any,
    color: "#9E9E9E",
    width: "17px",
    marginLeft: "4px",
  },
  quantityIconError: {
    position: "absolute" as any,
    color: "#D73912",
    width: "17px",
    marginLeft: "4px",
  },
});

const cache = new CellMeasurerCache({
  defaultHeight: 25,
  fixedWidth: true,
  fixedHeight: false,
});

interface numeric {
  decimalScale: number;
}

type IProps = {
  classes: any;
  treeReference: Boolean;
  columns: Array<{
    label: string;
    dataKey: string;
    ref: string;
    indentation?: boolean;
    disabled?: any;
    icon?: { condition: Function; component: React.ComponentClass<any> };
    link?: { condition: Function; url: Function };
    align: "center" | "left" | "right";
    width?: number;
    numeric?: numeric;
    setText?: Function;
    paddingRight?: any;
  }>;
  onChange: any;
  onFocus: any;
  onRowClick: Function;
  styleLine?: Array<{
    className: string;
    condition: Function;
  }>;
};

let valueToSave: any = undefined;
let updateTableTimeoutID: NodeJS.Timeout | undefined = undefined;

/** Largura atual da tabela da EAP. */
let currentTableWidth: number = 0;
/** Index atual da última linha visível da EAP, usada para evitar que o scroll sai da posição correta. */
let lastStopIndexRendered: number = 0;

@observer
class WBSTable extends React.Component<IProps> {
  state = {
    currentClmn: "",
    numericInputValue: -1,
    lineSelected: "",
    alertOpen: false,
  };

  /** Referência do componente List que monta o body da tabela. Usado
   * para forçar a atualização das alturas das linhas.
   */
  private _listBodyRef: any = undefined;

  constructor(props) {
    super(props);
    disableScroll();
  }

  componentDidMount() {
    let body = document.querySelector("body");
    if (body) {
      body.addEventListener("keyup", this.checkTabPress);
    }
    cache.clearAll();

    cache.clearAll();

    if (DevelopmentStore.redirectId) {
      DevelopmentStore.redirectId = "";
    }
    if (DevelopmentStore.needUpdateEapValues) {
      DevelopmentStore.needUpdateEapValues = false;
      setTimeout(() => {
        WBSStore.changeDecimalAndRule();
      }, 500);
    }

    if (!WBSStore.loadingTaskUpload) {
      WBSStore.resetUpdateCompositions();
      WBSStore.resetCompositionUpdateData();
    }
  }

  getNumeric(value, decimalScale) {
    if (value == NaN || value == undefined || value == "0.00") {
      value = 0;
    }

    if (decimalScale == NaN || decimalScale == undefined) {
      decimalScale = 0;
    }

    value = roundBy(value, WBSPropertiesStore.getRules(decimalScale));
    return formatNumber(value, decimalScale, decimalScale);
  }

  handleInputFocus = (value, hash, numeric?) => (e) => {
    let dataKey = hash.split("/")[2];
    WBSStore.selectedColumn = dataKey;
    this.state = WBSStore.handleInputFocus(this.state, value, hash, e, numeric);
  };

  handleNumericInputChange =
    (hash) =>
    ({ floatValue }) => {
      if (this.state.currentClmn === hash)
        this.state.numericInputValue = floatValue ? floatValue : 0;
    };

  async handleInputBlur(index: number, line, ref, dataKey, hash, numeric?) {
    const hasChanged =
      valueToSave &&
      valueToSave.index === index &&
      valueToSave.nameValue === dataKey;

    let oldValueToSave = valueToSave;
    valueToSave = undefined;
    this.state.currentClmn = await WBSStore.handleInputBlur(
      line,
      ref,
      dataKey,
      hash,
      this.state.currentClmn,
      this.props.onChange,
      oldValueToSave,
      false,
      numeric
    );

    if (hasChanged && dataKey === "description") {
      setTimeout(() => this.clearHeightCacheOfRow(index), 800);
    }
  }

  // event listener for keyup
  checkTabPress = (e) => {
    let line = e.target.id.split(".")[0];
    let column = e.target.id.split(".")[1];
    if (
      WBSStore.targetLoad.line == line &&
      WBSStore.targetLoad.column == column &&
      line != WBSStore.selectedLine.dataKey
    ) {
      return;
    }
    WBSStore.targetLoad = { line: line, column: column };

    if (WBSStore.display3D == "hidden") {
      ("use strict");
      // pick passed event or global event object if passed one is empty
      e = e || event;
      e.preventDefault();
      e.stopPropagation();
      let arrayItens = ["description", "quantity", "unit", "unitCost", "total"];
      if (e.keyCode == 9) {
        if (WBSStore.selectedLine.index == -1) return;
        if (e.shiftKey) {
          if (WBSStore.selectedLine.dataKey == "description") {
            WBSStore.setSelectedLine(WBSStore.selectedLine.index - 1, "total");
            WBSStore.selectedLine.dataKey = "total";
          } else {
            const foundIndex = arrayItens.findIndex(
              (element) => element == WBSStore.selectedLine.dataKey
            );
            WBSStore.selectedLine.dataKey = arrayItens[foundIndex - 1];
          }
        } else {
          if (WBSStore.selectedLine.dataKey == "total") {
            try {
              WBSStore.setSelectedLine(
                WBSStore.selectedLine.index + 1,
                "description"
              );
              WBSStore.selectedLine.dataKey = "description";
            } catch (error) {
              console.error(error);
            }
            WBSStore.targetLoad = "";
          } else {
            const foundIndex = arrayItens.findIndex(
              (element) => element == WBSStore.selectedLine.dataKey
            );
            WBSStore.selectedLine.dataKey = arrayItens[foundIndex + 1];
          }
        }
      }
    }
  };

  handleKeyPress = (index, dataKey) => (e) => {
    e = e || window.event;
    let key = "which" in e ? e.which : e.keyCode,
      activeElement = document.activeElement as any;
    if (key === 13) {
      this.killEvent(e);
      if (activeElement) activeElement.blur();

      if (index < WBSStore.dataTable.length - 1) {
        WBSStore.setSelectedLine(index + 1, dataKey, false);
      } else {
        ToolbarStore.createBlankWBSRow("bellow");
      }
    } else if (dataKey === "quantity" && key === 61) {
      this.killEvent(e);
      if (activeElement) activeElement.blur();

      if (WBSStore.rightMenuTab !== ERightMenuTab.PROPERTIES)
        WBSStore.rightMenuTab = ERightMenuTab.PROPERTIES;
      let formulaInput = document.getElementById("formulaInput");
      formulaInput && formulaInput.focus();
    }
  };

  handleKeyDown = (index, dataKey) => (e) => {
    WBSStore.handleKeyDown(index, dataKey, keyTO, ms, e);
  };

  killEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  handleChange(index, dataKey, ev) {
    const newValue = ev.target.value;
    valueToSave = { index, nameValue: dataKey, value: newValue };
  }

  /** Calcula a largura da tabela EAP de acordo com os menus laterais abertos
   * e a exibição ou não das colunas do BDI. Além disso ele já retorna a largura
   * correta para a coluna descrição. */
  getTableAndColumnsWidth(): { tableWidth: number; descColumnWidth: number } {
    const bdiIsShowing = WBSStore.WBS.data.bdi.show;
    const leftTabIsClosed = ProjectStore.leftTab === ELeftMenuTab.CLOSED;

    const percentColumnWidth = 106; // Tamanho da coluna %
    const columnsTotalWidth = this.props.columns.reduce(
      (sum, clm) =>
        sum +
        (clm.width ? Number(String(clm.width).replace("px", "")) || 0 : 0),
      0
    );

    let newTableWidth = 0;
    let descColumnWidth = 200;
    let minColumnWidth =
      columnsTotalWidth + percentColumnWidth + descColumnWidth;

    if (!leftTabIsClosed && !bdiIsShowing) {
      newTableWidth = window.innerWidth - 540;
    } else if (leftTabIsClosed && bdiIsShowing) {
      newTableWidth = window.innerWidth - 290 + 335;
    } else {
      newTableWidth = window.innerWidth - 290;
    }

    // Se o tamanho ficar pequeno demais para acomodar a coluna descrição
    // passa o tamanho mínimo esperado da tabela para a coluna descrição
    // com largura de 200px
    if (newTableWidth < minColumnWidth) {
      newTableWidth = minColumnWidth;
    } else {
      // Se não obtém a largura correta descontando as larguras das outras colunas
      descColumnWidth = newTableWidth - columnsTotalWidth - percentColumnWidth;
    }

    // Limpa o cache para atualizar as alturas da linhas ao mudar de largura
    if (newTableWidth !== currentTableWidth) {
      currentTableWidth = newTableWidth;
      cache.clearAll();
    }

    return {
      tableWidth: newTableWidth,
      descColumnWidth,
    };
  }

  /** Limpa a altura da linha informada salva no CellMeasurerCache e atualiza
   * as posições das linhas em tela.
   */
  clearHeightCacheOfRow(rowIndex: number) {
    const columnIndex = 0; // Pq é uma lista
    cache.clear(rowIndex, columnIndex);
    this.recomputeRowsHeight();
  }

  /** Força o componente List a alturaluzar as posições das linhas de acordo
   * com as novas alturas das mesmas.
   */
  recomputeRowsHeight() {
    if (WBSStore.task) return;

    // Força a atualização da altura das linhas da tabela
    if (updateTableTimeoutID) clearTimeout(updateTableTimeoutID);
    updateTableTimeoutID = setTimeout(() => {
      if (!this._listBodyRef) return;

      // Obtém o index da primeira linha em exibição para não atrapalhar o scroll da tabela
      const rowsRef = document.querySelectorAll("#wbsTableBody tr");
      let index = rowsRef && rowsRef[0] ? Number(rowsRef[0].id) || 0 : 0;
      this._listBodyRef.recomputeRowHeights(index);
    }, 200);
  }

  changeStatus() {
    WBSStore.alertOpen = false;
  }
  unlinkComposition(line) {
    this.setState({ lineSelected: line });
    WBSStore.alertOpen = true;
  }

  render() {
    const { classes, columns } = this.props;
    const { dataTable } = WBSStore,
      rowClasses = { root: classes.rowRoot },
      cellClasses = { root: classes.cellRoot };

    const selectedLine = WBSStore.selectedLine;
    let selectedLineFirstIndex =
      ToolbarStore.findModelMapMainLineIndex(selectedLine);
    let styleBdiInput;

    const { tableWidth, descColumnWidth } = this.getTableAndColumnsWidth();
    const scrollToIndex = WBSStore.scrollToIndex;
    const loading =
      !WBSStore.shouldUpdate && TakeOffStore.statusModelmap !== "Loading";

    const isLoading = () => {
      // PRUBK-799: Inseri no IF a verificação se MP está em Loadin, caso não esteja ele permite que o loade apareça
      if (loading) {
        // Limpa o cache pq ocorreu alguma alteração na estrutura da EAP e precisa medir as alturas novamente
        cache.clearAll();
        return (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              backgroundColor: "rgb(250, 250, 250)",
              width: "100%",
              height: "calc(100% + 30px)",
              display: "flex",
              margin: "auto",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress
              className={classes.progress}
              variant="indeterminate"
            />
            <Typography
              style={{
                position: "absolute",
                marginTop: "170px",
                color: "#ABC200",
              }}
            >
              {i18n.wbs.calculating.subtitle}...
            </Typography>
          </div>
        );
      }
      return <></>;
    };

    let allParentModel: any = [];
    let parentModel;

    for (let index = 0; index < dataTable.length; index++) {
      if (parentModel) {
        if (dataTable[index].data.fromModelMap) {
          if (parentModel.id !== dataTable[index].data.fromModelMap)
            parentModel = { id: dataTable[index].data.fromModelMap, index };
          else {
            if (
              ToolbarStore.findModelMapMainLineIndex(dataTable[index]) !=
              parentModel.index
            ) {
              parentModel = { id: dataTable[index].data.fromModelMap, index };
            } else {
              parentModel = {
                id: dataTable[index].data.fromModelMap,
                index: parentModel.index,
              };
            }
          }
        } else parentModel = false;
      } else if (dataTable[index].data.fromModelMap)
        parentModel = { id: dataTable[index].data.fromModelMap, index };

      allParentModel[index] = parentModel;
    }
    const lineRenderOld = (line, index) => (
      <TableRow classes={rowClasses} key={index} id={index}>
        {columns.map((clmn) => {
          if (DevelopmentStore.firstTimeOpenEAP && index == 1) {
            DevelopmentStore.firstTimeOpenEAP = false;
            WBSStore.setSelectedLine(0, "description", false);
          }

          return columnsRender(
            clmn,
            index,
            line,
            allParentModel != undefined ? allParentModel[index] : false,
            allParentModel != undefined && allParentModel[index] != false
              ? allParentModel.filter(
                  (a) =>
                    a &&
                    allParentModel[index] &&
                    a.index == allParentModel[index].index
                ).length - 1
              : undefined
          );
        })}
      </TableRow>
    );

    const lineRender = ({
      key, // Unique key within array of rows
      index, // Index of row within collection
      style, // Style object to be applied to row (to position it)
      parent,
    }) => (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ registerChild }) => (
          <div ref={registerChild} key={key} style={style}>
            {lineRenderOld(dataTable[index], index)}
          </div>
        )}
      </CellMeasurer>
    );

    const columnsRender = (
      clmn,
      index,
      line,
      parentModelTmp,
      parentModelTmpCount
    ) => {
      const { icon, link, align, ref, dataKey, setText, indentation } = clmn;

      const isBdi = WBSStore.WBS.data.bdi.show;
      const tree = line.data.tree;
      const isStage = line.data.rowType === ERowType.STAGE;
      const isBlankLine = line.data.rowType === ERowType.BLANK;

      // É necessário desabilitar o input pq a tabela continua sendo afetada mesmo com a tabela da composição aberta
      const descriptionInputEnabled =
        !WBSStore.task && selectedLine ? selectedLine.index == index : false;

      let Icon;
      let url; // URL para entrar no conteúdo da composição
      let width = clmn.width;
      let disabled = clmn.disabled;
      let numeric = clmn.numeric;
      let totalPercentage = 0;
      let paddingLeft = 8;
      let value = line.data[dataKey];
      let hash = [index, ref, dataKey].join("/");

      // Altera o valor da coluna
      if (setText) value = setText(line);

      // Define o icone e a URL da composição
      if (icon && icon.condition(line, tree)) Icon = icon.component;
      if (link && link.condition(line)) url = link.url(line);

      // Acrescenta o padding de acordo com a identação e o icone
      if (indentation) paddingLeft += (tree.split(".").length - 1) * 15;
      if (Icon) paddingLeft += 15;

      // Executa a função para definir o valor de disabled
      if (disabled && typeof disabled === "function")
        disabled = disabled(line, tree);

      // Define o valor de porcentagem da linha
      if ((isBdi && dataKey === "bdi") || (!isBdi && dataKey === "total")) {
        let totalLocal = line.data.total;
        if (!totalLocal) {
          totalLocal = 0;
        }
        const tmpTotalCost = WBSStore.currentWbsTotalCost.toFixed(2);
        const tmpValue = roundBy(totalLocal, WBSPropertiesStore.getRules(2));
        totalPercentage = Number(tmpTotalCost)
          ? (tmpValue * 100) / Number(tmpTotalCost)
          : 0;
        totalPercentage = isNaN(totalPercentage) ? 0 : totalPercentage;
      }

      // Define o estilo da coluna BDI e define o valor padrão
      // para o campo caso não tenha um percentual para a linha
      if (!isBlankLine && dataKey === "bdi" && value === undefined) {
        value = WBSStore.WBS.data.bdi.percentual;
        styleBdiInput = { fontStyle: "italic", fontWeight: "normal" };
      } else {
        styleBdiInput = { fontStyle: "normal", fontWeight: "bold" };
      }

      // Limpa os campos que não devem ser preenchidos em etapas
      if (
        ["quantity", "unit", "unitCost", "vendaBdi", "bdi"].indexOf(dataKey) >
          -1 &&
        isStage
      ) {
        value = "";
      }

      // Desabilita o campo quantidade quando tiver fórmula ou vínculo com model-map
      if (
        selectedLine.data &&
        ((selectedLine.data.fromModelMap && dataKey === "quantity") ||
          (selectedLine.data.formula &&
            selectedLine.data.formula !== "" &&
            selectedLine.index === index &&
            dataKey === "quantity"))
      ) {
        disabled = true;
      }

      // DEFINE OS ESTILOS DAS LINHAS
      let styles: any = {};
      // Define o estilo das linhas com quantitantivo extraído de model-map
      if (
        parentModelTmp &&
        selectedLine.data &&
        selectedLine.data.fromModelMap &&
        selectedLine.data.takeOffQuantity &&
        selectedLine.data.takeOffQuantity.length > 0 &&
        selectedLine.data.fromModelMap === parentModelTmp.id &&
        selectedLineFirstIndex == parentModelTmp.index &&
        (dataKey === "quantity" || dataKey === "description")
      ) {
        if (dataKey === "description") {
          styles = {
            width: width ? width : "100%",
            minWidth: width,
            borderRight: "2px dashed #ABC200",
          };
        } else {
          styles = {
            width: width ? width : "100%",
            minWidth: width,
            border: "2px dashed #ABC200",
            backgroundColor: "rgba(171, 194, 0, 0.1)",
          };
        }
      }
      // Define o estilo das linhas pertencentes a model-map full
      else if (
        parentModelTmp &&
        selectedLine.data &&
        selectedLine.data.fromModelMap &&
        selectedLine.data.fromModelMap === parentModelTmp.id &&
        selectedLineFirstIndex == parentModelTmp.index &&
        (!selectedLine.data.takeOffQuantity ||
          selectedLine.data.takeOffQuantity.length === 0) &&
        ["description", "quantity", "unit", "link"].indexOf(dataKey) > -1
      ) {
        if (dataKey == "link") {
          styles = {
            width: width ? width : "100%",
            minWidth: width,
            borderRight: "2px dashed #ABC200",
          };
        } else {
          styles = {
            width: width ? width : "100%",
            minWidth: width,
            borderRight: dataKey === "unit" ? "2px dashed #ABC200" : "",
            borderTop:
              line.index != parentModelTmp.index ? "" : "2px dashed #ABC200",
            borderBottom:
              line.index != parentModelTmp.index + parentModelTmpCount
                ? ""
                : "2px dashed #ABC200",
            backgroundColor: "rgba(171, 194, 0, 0.1)",
          };
        }
      }
      // Aplica formatação apenas na célula de quantidade se identificar que foi aplicada fórmula para cálculo
      else if (
        selectedLine.data &&
        (selectedLine.data.quantity || selectedLine.data.quantity !== "") &&
        selectedLine.index === index &&
        dataKey === "quantity"
      ) {
        styles = {
          width: width ? width : "100%",
          minWidth: width,
          // A borda aplicada pode ser verde ou vermelha se, respectivamente, a fórmula estiver válida ou inválida
          border: !selectedLine.data.formula
            ? ""
            : WBSPropertiesStore.validateFormula(
                selectedLine.data.formula,
                selectedLine.data.quantity
              )
            ? "2px dashed #ABC200"
            : "2px dashed #c20000",
        };
        // Define o estilo dos campos de código
      } else if (dataKey == "tree") {
        styles = {
          backgroundColor: "rgba(0,0,0,0.02)",
          width: WBSStore.maxCodeEapLength,
          minWidth: WBSStore.maxCodeEapLength,
          paddingLeft: 12,
        };
        // Define o estilo padrão
      } else {
        if (dataKey === "description") width = descColumnWidth;
        styles = {
          width: width && width > 0 ? width : "100%",
          minWidth: width,
          border: "",
        };
      }

      // Define o estilo de linha selecionada
      if (selectedLine.index == index) {
        styles.backgroundColor = "rgba(171, 194, 0, 0.2)";
      }

      return (
        <>
          <TableCell
            key={dataKey}
            onMouseDown={() => {
              let activeElement = document.activeElement as any;
              if (activeElement) activeElement.blur();
              WBSStore.setSelectedLine(index, dataKey, false);
            }}
            classes={cellClasses}
            className={classNames(
              {
                [classes.relative]: !!Icon,
                [classes.cellRootDisabled]: disabled,
              },
              ...(!!parentModelTmp &&
                ((parentModelTmp.id + "." + parentModelTmp.index) as any))
            )}
            {...(parentModelTmp && { id: parentModelTmp.id + "." + dataKey })}
            {...(styles && { style: styles })}
          >
            {dataKey === "quantity" ? (
              line.data ? (
                line.data.formula ? (
                  WBSPropertiesStore.validateFormula(
                    line.data.formula.replace("=", ""),
                    line.data.quantity
                  ) ? (
                    ""
                  ) : line.data.takeOffQuantity ? (
                    ""
                  ) : (
                    <Error
                      className={
                        line.id == selectedLine.id
                          ? classes.quantityIconError
                          : classes.quantityIcon
                      }
                    />
                  )
                ) : (
                  ""
                )
              ) : (
                ""
              )
            ) : (
              ""
            )}

            {Icon && (
              <Icon
                style={{ left: paddingLeft - 20 + "px" }}
                className={classes.icon}
              />
            )}
            {dataKey === "link" && !isBlankLine ? (
              <LinkIcon
                className={classNames(
                  `link.${line.id}`,
                  selectedLine &&
                    selectedLine.index != -1 &&
                    line.data &&
                    line.data.contentId &&
                    line.data.contentId == selectedLine.data.contentId &&
                    WBSStore.WBSRows.filter(
                      (r) => r.data.contentId == line.data.contentId
                    ).length > 1
                    ? classes.linkIconEnabled
                    : classes.linkIcon
                )}
                cursor="pointer"
                onClick={() => this.unlinkComposition(line)}
              />
            ) : url && !isBlankLine ? (
              <Link
                className={classes.link}
                style={{
                  paddingLeft: paddingLeft + "px",
                  ...(align && {
                    justifyContent: align === "right" ? "flex-end" : align,
                  }),
                }}
                to={url}
                onClick={() => {
                  DevelopmentStore.firstTimeOpenTaskTable = true;
                  WBSStore.getLineIndex = index;
                  WBSStore.setEAPTableScrollToIndex(index);
                  ToolbarTaskStore.resetStore();
                }}
              >
                {numeric ? this.getNumeric(value, numeric.decimalScale) : value}
              </Link>
            ) : numeric && !isBlankLine ? (
              <NumberFormat
                {...numeric}
                readOnly={disabled}
                autoComplete={"off"}
                ref={(inst) =>
                  (WBSStore.refCollection[index + dataKey] =
                    ReactDOM.findDOMNode(inst))
                }
                onChange={(v) => this.handleChange(index, dataKey, v)}
                id={`${index}.${dataKey}`}
                value={
                  numeric
                    ? _.isNumber(value)
                      ? roundBy(
                          value,
                          WBSPropertiesStore.getRules(numeric.decimalScale)
                        )
                      : value
                    : value
                }
                className={classNames(classes.input, {
                  [classes.cellDisabled]: disabled,
                })}
                style={{
                  ...(dataKey === "bdi" ? styleBdiInput : ""),
                  paddingLeft: paddingLeft + "px",
                  ...(isStage && { fontWeight: 700 }),
                  ...(align && { textAlign: align }),
                }}
                {...(i18n.language === "PT"
                  ? { decimalSeparator: ",", thousandSeparator: "." }
                  : { decimalSeparator: ".", thousandSeparator: "," })}
                {...(!disabled && {
                  onValueChange: this.handleNumericInputChange(hash),
                  onBlur: async () =>
                    await this.handleInputBlur(
                      index,
                      line,
                      ref,
                      dataKey,
                      hash,
                      true
                    ),
                })}
                onFocus={this.handleInputFocus(value, hash, true)}
                onKeyPress={this.handleKeyPress(index, dataKey)}
                onKeyDown={this.handleKeyDown(index, dataKey)}
                onKeyUp={() => {
                  clearTimeout(keyTO);
                  keyTO = null;
                }}
              />
            ) : dataKey === "description" ? (
              <DescriptionCell
                id={`${index}.${dataKey}`}
                rowHeight={cache.rowHeight({ index })}
                defaultRowHeight={25}
                selected={descriptionInputEnabled}
                onGetTextAreaRef={(inst) => {
                  WBSStore.refCollection[index + dataKey] = inst;
                }}
                className={classNames({
                  [classes.cellDisabled]: disabled,
                })}
                readOnly={disabled}
                value={isBlankLine ? "" : value}
                onChange={(ev) => this.handleChange(index, dataKey, ev)}
                onFocus={this.handleInputFocus(value, hash)}
                onBlur={async () =>
                  await this.handleInputBlur(
                    index,
                    line,
                    ref,
                    dataKey,
                    hash,
                    undefined
                  )
                }
                onResizeHeight={() => this.clearHeightCacheOfRow(index)}
                onKeyPress={this.handleKeyPress(index, dataKey)}
                onKeyDown={this.handleKeyDown(index, dataKey)}
                onKeyUp={() => {
                  clearTimeout(keyTO);
                  keyTO = null;
                }}
                style={{
                  width: descColumnWidth,
                  padding: "5px 10px",
                  paddingLeft: paddingLeft + "px",
                  ...(isStage && {
                    fontWeight: 700,
                    textTransform: "uppercase" as any,
                  }),
                  ...(align && { textAlign: align }),
                }}
              />
            ) : (
              <input
                type={"text"}
                readOnly={disabled}
                ref={(inst) => {
                  WBSStore.refCollection[index + dataKey] = inst;
                }}
                autoComplete={"off"}
                onChange={(v) => this.handleChange(index, dataKey, v)}
                {...(selectedLine.index == index
                  ? {
                      defaultValue: value, // Este campo permite edição
                    }
                  : {
                      value:
                        !isBlankLine || (dataKey == "tree" && isBlankLine)
                          ? value
                          : "", // Este permite atualizar os valores quando o insumo foi vinculado a outro que foi editado
                    })}
                id={`${index}.${dataKey}`}
                className={classNames(classes.input, {
                  [classes.cellDisabled]: disabled,
                })}
                onFocus={this.handleInputFocus(value, hash)}
                onBlur={async () =>
                  await this.handleInputBlur(index, line, ref, dataKey, hash)
                }
                style={{
                  padding: 0,
                  paddingLeft: paddingLeft + "px",
                  ...(isStage && {
                    fontWeight: 700,
                    textTransform: "uppercase" as any,
                  }),
                  ...(align && { textAlign: align }),
                }}
                onKeyPress={this.handleKeyPress(index, dataKey)}
                onKeyDown={this.handleKeyDown(index, dataKey)}
                onKeyUp={() => {
                  clearTimeout(keyTO);
                  keyTO = null;
                }}
              />
            )}
          </TableCell>
          {((isBdi && dataKey === "bdi") ||
            (!isBdi && dataKey === "total")) && (
            <TableCell
              onMouseDown={() => {
                let activeElement = document.activeElement as any;
                if (activeElement) activeElement.blur();
                WBSStore.setSelectedLine(index, dataKey, false);
              }}
              key={`${index}.percent`}
              id={`${index}.percent`}
              classes={cellClasses}
              className={classNames(
                classes.cellRootDisabled,
                { [classes.relative]: !!Icon },
                ...(!!parentModelTmp &&
                  ((parentModelTmp.id + "." + parentModelTmp.index) as any))
              )}
              {...(styles
                ? { style: { ...styles, minWidth: "107px" } }
                : { style: { minWidth: "107px" } })}
            >
              <div
                style={{
                  position: "relative",
                  height: "22px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    width: `${totalPercentage}%`,
                    backgroundImage:
                      "linear-gradient(to right, rgba(171,194,0, 1), rgba(171,194,0, 0.1))",
                    height: "22px",
                    top: 1,
                    zIndex: 1,
                  }}
                ></div>
                <input
                  type={"text"}
                  readOnly={true}
                  value={`${totalPercentage.toFixed(2)}%`}
                  className={classNames(classes.input, {
                    [classes.cellDisabled]: disabled,
                  })}
                  style={{
                    textAlign: align,
                    position: "absolute",
                    zIndex: 10,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    ...(isStage && { fontWeight: 700 }),
                  }}
                  onKeyPress={this.handleKeyPress(index, dataKey)}
                  onKeyDown={this.handleKeyDown(index, dataKey)}
                  onKeyUp={() => {
                    clearTimeout(keyTO);
                    keyTO = null;
                  }}
                  onFocus={this.handleInputFocus(value, hash)}
                />
              </div>
            </TableCell>
          )}
        </>
      );
    };

    const headerColumns = ({ key }) => {
      const clmElements = columns.map(
        ({ dataKey, label, align, width, paddingRight }) => {
          let style: any = {};
          if (align) style.textAlign = align;
          if (width) {
            style.minWidth =
              dataKey === "description" ? descColumnWidth : width;
            style.width = dataKey === "description" ? descColumnWidth : width;
          } else {
            style.width = "100%";
          }
          if (paddingRight) style.paddingRight = paddingRight;
          return (
            <TableCell key={key} {...{ style }}>
              {label}
            </TableCell>
          );
        }
      );
      clmElements.push(
        <TableCell
          className={classNames(classes.nowRap, classes.thRef)}
          style={{ width: "112px", minWidth: "112px", textAlign: "center" }}
        >
          %
        </TableCell>
      );
      return clmElements;
    };

    // Impede que o blur seja executado logo ao renderizar a tabela
    let cancelBlurOnFirstScroll = true;

    return (
      <React.Fragment>
        {isLoading()}
        <ScrollSync>
          {({ onScroll, scrollLeft }) => (
            <div
              style={{
                overflow: "scroll",
                width:
                  ProjectStore.leftTab !== ELeftMenuTab.CLOSED
                    ? window.innerWidth - 533
                    : window.innerWidth - 283,
              }}
            >
              <Table className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow classes={rowClasses}>
                    <List
                      width={tableWidth}
                      height={35}
                      rowCount={1}
                      rowHeight={35}
                      rowRenderer={headerColumns}
                      overscanRowCount={0}
                      scrollLeft={scrollLeft}
                      onScroll={onScroll}
                    />
                  </TableRow>
                </TableHead>
              </Table>
              <div
                className={classNames(classes.calcHeight)}
                ref={(node) => (WBSStore.tableBody = node)}
              >
                <Table className={classes.table}>
                  <TableBody className={classes.tableBody} id="wbsTableBody">
                    <List
                      ref={(el) => {
                        this._listBodyRef = el;
                      }}
                      width={tableWidth}
                      height={
                        LoginStore.isTrialUser()
                          ? window.innerHeight - 285
                          : window.innerHeight - 241
                      }
                      rowCount={dataTable.length}
                      rowHeight={cache.rowHeight}
                      deferredMeasurementCache={cache}
                      rowRenderer={lineRender}
                      overscanRowCount={10} // Caso esteja muito lento diminuir este numero
                      scrollToIndex={
                        scrollToIndex ? scrollToIndex : lastStopIndexRendered
                      }
                      scrollLeft={scrollLeft}
                      onRowsRendered={({ stopIndex }) => {
                        lastStopIndexRendered = stopIndex || 0;
                      }}
                      onScroll={() => {
                        WBSStore.clearEAPTableScrollToIndex();
                        if (valueToSave && !cancelBlurOnFirstScroll) {
                          const ref =
                            WBSStore.refCollection[
                              valueToSave.index + valueToSave.nameValue
                            ];
                          if (ref) ref.blur();
                        }
                        if (cancelBlurOnFirstScroll)
                          cancelBlurOnFirstScroll = false;
                      }}
                    />
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </ScrollSync>
        {WBSStore.alertOpen && (
          <Alert
            startOpen={WBSStore.alertOpen}
            unlinkComposition={WBSStore.unlinkComposition}
            lineSelected={this.state.lineSelected}
            close={this.changeStatus}
          />
        )}
      </React.Fragment>
    );
  }
}

export default withStyles(styles)(WBSTable);



// WEBPACK FOOTER //
// ./src/modules/WBS/components/layouts/WBSTable.tsx